import { z } from 'zod';
import { MerchantEligibilityStateEnum, MerchantEligibilityStateSchema, } from './api-business';
import { ContactEmailSchema } from './contact';
import { AccountNotDirectEligibleReasonEnum, AccountNotDirectEligibleReasonSchema, } from './errors';
import { ConnectedAccountConfigurationSnapshotSchema, ConnectedAccountProfileEnum, } from './execution-scope';
import { PXE_FIXTURE_PAYMENT_DOMAINS_MISSING, PXE_FIXTURE_PAYMENT_DOMAINS_VERIFIED, PaymentMethodDomainEligibilitySchema, } from './payment-domains';
import { BusinessIdSchema, IsoTimestampSchema, StripeAccountIdSchema, } from './primitives';
/**
 * Public Experiences Part 2 — super-admin managed-account migration contract
 * (TYPE-P2-05, owner scope addition 2026-09-14, DEC-P2-16).
 *
 * Stripe cannot reconfigure an existing connected account: the fee payer and
 * the dashboard type are fixed at creation, and Stripe's migration guide says
 * "Stripe doesn't support directly changing the type of an existing connected
 * account. Instead, you need to create new connected accounts … and
 * re-onboard". A business whose online account is a legacy `type: 'custom'`
 * account (`controller.fees.payer = application_custom`, profile
 * `LEGACY_CUSTOM`) therefore needs a NEW `POINTCRAFT_MANAGED_DIRECT` account
 * (`controller: fees.payer=application, losses.payments=application,
 * requirement_collection=application, stripe_dashboard.type=none`) before it
 * can take direct-charge Payforms.
 *
 * The pathway is three super-admin calls:
 *
 *  1. `GET  …/payforms-eligibility` — the live direct-charge verdict for the
 *     currently linked account (`AdminPayformsEligibilityResponseSchema`).
 *  2. `POST …/managed-account-migration/preview` — reads the linked legacy
 *     account, its persons and its external accounts (read-only) and returns
 *     everything that can be prefilled PLUS the exact inputs Stripe never
 *     returns and the admin must re-supply (`ManagedAccountMigrationPreviewSchema`).
 *  3. `POST …/managed-account-migration` — creates the managed account with the
 *     supplied inputs, creates the persons, attaches the bank account, relinks
 *     the business to the new account and refetches it
 *     (`ManagedAccountMigrationRequestSchema` → `ManagedAccountMigrationResultSchema`).
 *
 * The legacy account is never modified, deauthorized or deleted: it keeps its
 * history and simply stops being the business's linked online account. The
 * migration is idempotent per (business, source account): a retry with the same
 * request never creates a second account.
 *
 * Browser-safe: zod and sibling contract files only.
 */
// ---------------------------------------------------------------------------
// Routes
// ---------------------------------------------------------------------------
export const ADMIN_STRIPE_ACCOUNT_MIGRATION_ROUTE_PREFIX = '/api/admin/business/v3/stripe';
/** Frozen route patterns. All three are super-admin (`validateAdmin`). */
export const ADMIN_STRIPE_ACCOUNT_MIGRATION_ROUTES = {
    /** GET — live direct-charge eligibility of the linked account. */
    payformsEligibility: '/api/admin/business/v3/stripe/:businessId/payforms-eligibility',
    /** POST (no body) — read-only preview of the migration. */
    migrationPreview: '/api/admin/business/v3/stripe/:businessId/managed-account-migration/preview',
    /** POST — execute the migration (rate limited: 5 per minute). */
    migrate: '/api/admin/business/v3/stripe/:businessId/managed-account-migration',
    /**
     * GET — payment-method domain component for the linked account
     * (DEC-P2-18; Stripe's current registrations, super-admin).
     */
    paymentDomains: '/api/admin/business/v3/stripe/:businessId/payment-domains',
    /**
     * POST `{ confirm: true }` — register / re-enable the required domains on
     * the linked account (super-admin; rate limited: 10 per minute).
     */
    paymentDomainsEnsure: '/api/admin/business/v3/stripe/:businessId/payment-domains/ensure',
};
const withBusinessId = (pattern, businessId) => pattern.replace(':businessId', encodeURIComponent(businessId));
export const buildAdminPayformsEligibilityPath = (businessId) => withBusinessId(ADMIN_STRIPE_ACCOUNT_MIGRATION_ROUTES.payformsEligibility, businessId);
export const buildManagedAccountMigrationPreviewPath = (businessId) => withBusinessId(ADMIN_STRIPE_ACCOUNT_MIGRATION_ROUTES.migrationPreview, businessId);
export const buildManagedAccountMigrationPath = (businessId) => withBusinessId(ADMIN_STRIPE_ACCOUNT_MIGRATION_ROUTES.migrate, businessId);
export const buildAdminPaymentDomainsPath = (businessId) => withBusinessId(ADMIN_STRIPE_ACCOUNT_MIGRATION_ROUTES.paymentDomains, businessId);
export const buildAdminPaymentDomainsEnsurePath = (businessId) => withBusinessId(ADMIN_STRIPE_ACCOUNT_MIGRATION_ROUTES.paymentDomainsEnsure, businessId);
// ---------------------------------------------------------------------------
// Shared value schemas
// ---------------------------------------------------------------------------
/** The only profile a migration can target. Immutable once the account exists. */
export const MANAGED_ACCOUNT_MIGRATION_TARGET_PROFILE = ConnectedAccountProfileEnum.POINTCRAFT_MANAGED_DIRECT;
export const ManagedAccountMigrationTargetProfileSchema = z.literal(MANAGED_ACCOUNT_MIGRATION_TARGET_PROFILE);
/** Postal address as the onboarding v2 route already accepts it. */
export const MigrationAddressSchema = z
    .object({
    street1: z.string().trim().min(1),
    street2: z.string().trim().nullable(),
    city: z.string().trim().min(1),
    state: z.string().trim().min(1),
    postalCode: z.string().trim().min(1),
    country: z.string().trim().min(2).max(2),
})
    .strict();
export const MigrationDobSchema = z
    .object({
    day: z.number().int().min(1).max(31),
    month: z.number().int().min(1).max(12),
    year: z.number().int().min(1900),
})
    .strict();
/**
 * Contact email at the migration boundary: the same normalization as every
 * other Public Experiences email (trim, lowercase, RFC-ish validation).
 */
export const MigrationContactEmailSchema = ContactEmailSchema;
/**
 * Contact phone at the migration boundary. Deliberately lenient (digits,
 * spaces, `+ - ( ) .`): the backend normalizes it with its existing
 * `OnboardingPhoneSchema` (`src/utilities/onboarding-contact.ts`), which
 * accepts legacy US formatting and explicit E.164 and rejects the rest — the
 * same rule the onboarding v2 route applies. Browser code MUST NOT try to
 * normalize; it forwards what the operator typed.
 */
export const MIGRATION_PHONE_MAX_LENGTH = 24;
export const MigrationContactPhoneSchema = z
    .string()
    .trim()
    .min(7)
    .max(MIGRATION_PHONE_MAX_LENGTH)
    .regex(/^[+\d(][\d\s().-]*$/, 'phone may contain digits, spaces and + - ( ) .');
export const MigrationBankHolderTypeSchema = z.enum(['individual', 'company']);
/**
 * The person's role on the account, as Stripe reports it on the source person
 * and as the request repeats it for the new person. Exactly one person must be
 * the representative.
 */
export const MigrationPersonRelationshipSchema = z
    .object({
    representative: z.boolean(),
    owner: z.boolean(),
    executive: z.boolean(),
    title: z.string().trim().min(1).max(100).nullable(),
    percentOwnership: z.number().min(0).max(100).nullable(),
})
    .strict();
// ---------------------------------------------------------------------------
// 1. Admin Payforms eligibility (GET …/payforms-eligibility)
// ---------------------------------------------------------------------------
/**
 * The super-admin projection of `resolveDirectChargeEligibility({ live: true })`
 * for the business's currently linked online Stripe account. Unlike the
 * merchant DTO it carries the account id, the controller snapshot and an
 * operator-safe message (never a provider error body).
 *
 *  - `ELIGIBLE`      — the linked account can take direct-charge Payforms.
 *  - `NOT_ELIGIBLE`  — `reason` says why (`LEGACY_ACCOUNT_CONFIGURATION` is the
 *                      case this migration pathway exists for).
 *  - `UNAVAILABLE`   — Stripe could not be read; not a verdict.
 */
export const AdminPayformsEligibilityResponseSchema = z
    .object({
    state: MerchantEligibilityStateSchema,
    /** Present exactly when `state` is NOT_ELIGIBLE. */
    reason: AccountNotDirectEligibleReasonSchema.nullable(),
    /** Operator-safe explanation (`null` when ELIGIBLE). */
    message: z.string().max(500).nullable(),
    /** The linked `acct_…`, `null` when no account is linked. */
    accountId: StripeAccountIdSchema.nullable(),
    /** Controller snapshot from the live read, `null` when unavailable. */
    snapshot: ConnectedAccountConfigurationSnapshotSchema.nullable(),
    /** `true` when the verdict came from a live `accounts.retrieve`. */
    live: z.boolean(),
    checkedAt: IsoTimestampSchema,
    /**
     * Payment-method domain component (DEC-P2-18). Absent when the verdict
     * stopped before the profile check (NOT_LINKED, DIRECT_DISABLED,
     * CHARGES_DISABLED) or when the provider could not be read at all.
     */
    paymentDomains: PaymentMethodDomainEligibilitySchema.optional(),
})
    .strict()
    .superRefine((response, ctx) => {
    const needsReason = response.state === MerchantEligibilityStateEnum.NOT_ELIGIBLE;
    if (needsReason !== (response.reason !== null)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['reason'],
            message: 'reason is required exactly when state is NOT_ELIGIBLE',
        });
    }
});
// ---------------------------------------------------------------------------
// 2. Migration preview (POST …/managed-account-migration/preview)
// ---------------------------------------------------------------------------
export var ManagedAccountMigrationStatusEnum;
(function (ManagedAccountMigrationStatusEnum) {
    /** The linked account is legacy; the migration can be executed. */
    ManagedAccountMigrationStatusEnum["READY"] = "READY";
    /** The linked account already has `fees.payer = application`; nothing to do. */
    ManagedAccountMigrationStatusEnum["ALREADY_MANAGED"] = "ALREADY_MANAGED";
    /** The business has no enabled online Stripe account to migrate from. */
    ManagedAccountMigrationStatusEnum["NOT_LINKED"] = "NOT_LINKED";
    /** Stripe could not be read; the prefilled data is partial or absent. */
    ManagedAccountMigrationStatusEnum["UNAVAILABLE"] = "UNAVAILABLE";
})(ManagedAccountMigrationStatusEnum || (ManagedAccountMigrationStatusEnum = {}));
export const ManagedAccountMigrationStatusSchema = z.nativeEnum(ManagedAccountMigrationStatusEnum);
/**
 * Per-person inputs the admin must supply because Stripe never returns them
 * (`ssn`) or because the source person lacks them.
 *
 *  - `ssn`       — `ssn_last_4` / full SSN (`id_number`). Stripe only reports
 *                  `ssn_last_4_provided` / `id_number_provided` booleans, so
 *                  EVERY migrated person needs it re-entered.
 *  - the others  — copied from the source person when present; required only
 *                  when the source person lacks them (the new-person call needs
 *                  all of them).
 */
export const MigrationPersonInputKeySchema = z.enum([
    'ssn',
    'dob',
    'address',
    'firstName',
    'lastName',
    'email',
    'phone',
]);
/** Prefilled business fields read from the legacy account. */
export const MigrationPreviewBusinessSchema = z
    .object({
    name: z.string().nullable(),
    url: z.string().nullable(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    description: z.string().nullable(),
    mcc: z.string().nullable(),
    address: MigrationAddressSchema.nullable(),
    statementDescriptor: z.string().nullable(),
})
    .strict();
/**
 * A person on the legacy account. Everything readable is prefilled; `provided`
 * repeats Stripe's own "was it supplied" booleans so the UI can explain why
 * `ssn` is always in `requiredInputs`.
 */
export const MigrationPreviewPersonSchema = z
    .object({
    sourcePersonId: z.string().min(1),
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    dob: MigrationDobSchema.nullable(),
    address: MigrationAddressSchema.nullable(),
    relationship: MigrationPersonRelationshipSchema,
    /** Stripe's `*_provided` flags — booleans only, never the values. */
    provided: z
        .object({
        ssnLast4: z.boolean(),
        idNumber: z.boolean(),
        dob: z.boolean(),
        address: z.boolean(),
    })
        .strict(),
    requiredInputs: z.array(MigrationPersonInputKeySchema),
})
    .strict();
/** The readable part of the legacy external bank account (never the number). */
export const MigrationPreviewBankAccountSchema = z
    .object({
    holderName: z.string().nullable(),
    holderType: MigrationBankHolderTypeSchema.nullable(),
    routingNumber: z.string().nullable(),
    last4: z.string().length(4).nullable(),
    bankName: z.string().nullable(),
})
    .strict();
/**
 * One input the admin must supply. `key` is a dot path into
 * `ManagedAccountMigrationRequestSchema`, e.g. `businessTaxId`,
 * `persons[person_…].ssn`, `bankAccount.accountNumber`, `tosAcceptance`,
 * `business.mcc`.
 */
export const MigrationRequiredInputSchema = z
    .object({
    key: z.string().min(1),
    label: z.string().min(1),
    reason: z.string().min(1),
})
    .strict();
/** Top-level input keys that are ALWAYS required (never readable from Stripe). */
export const MIGRATION_ALWAYS_REQUIRED_INPUT_KEYS = [
    'businessTaxId',
    'bankAccount.accountNumber',
    'tosAcceptance',
];
/** Builds the `persons[<sourcePersonId>].<input>` key. */
export const buildMigrationPersonInputKey = (sourcePersonId, input) => `persons[${sourcePersonId}].${input}`;
export const ManagedAccountMigrationPreviewSchema = z
    .object({
    businessId: BusinessIdSchema,
    /** The currently linked (legacy) account; `null` when NOT_LINKED. */
    sourceAccountId: StripeAccountIdSchema.nullable(),
    sourceSnapshot: ConnectedAccountConfigurationSnapshotSchema.nullable(),
    targetProfile: ManagedAccountMigrationTargetProfileSchema,
    status: ManagedAccountMigrationStatusSchema,
    business: MigrationPreviewBusinessSchema,
    persons: z.array(MigrationPreviewPersonSchema),
    bankAccount: MigrationPreviewBankAccountSchema.nullable(),
    requiredInputs: z.array(MigrationRequiredInputSchema),
    warnings: z.array(z.string()),
    generatedAt: IsoTimestampSchema,
})
    .strict();
// ---------------------------------------------------------------------------
// 3. Migration request (POST …/managed-account-migration)
// ---------------------------------------------------------------------------
/** Editable overrides for the prefilled business fields. All optional. */
export const MigrationBusinessOverridesSchema = z
    .object({
    name: z.string().trim().min(2).max(240).optional(),
    url: z.string().trim().min(1).optional(),
    email: MigrationContactEmailSchema.optional(),
    phone: MigrationContactPhoneSchema.optional(),
    description: z.string().trim().min(1).optional(),
    mcc: z
        .string()
        .trim()
        .regex(/^\d{4}$/, 'mcc must be four digits')
        .optional(),
    address: MigrationAddressSchema.optional(),
    statementDescriptor: z.string().trim().min(1).max(22).optional(),
})
    .strict();
export const MIGRATION_SSN_LAST4_REGEX = /^\d{4}$/;
export const MIGRATION_FULL_SSN_REGEX = /^\d{9}$/;
/**
 * One person to create on the new account. `sourcePersonId` ties it to the
 * preview entry; readable fields are copied from the source unless overridden
 * here. `ssnLast4` or `fullSsn` is required for every listed person (Stripe
 * never returns either): a person who must not be re-created is simply left
 * out of `persons`. `fullSsn` is sent as Stripe `id_number` and its last four
 * digits as `ssn_last_4`; `idNumber` is accepted for non-SSN identifiers and,
 * when present without `fullSsn`, is sent as `id_number`. `driverId` is
 * carried for parity with onboarding v2 and is not sent to Stripe today.
 */
export const MigrationPersonRequestSchema = z
    .object({
    sourcePersonId: z.string().min(1),
    relationship: MigrationPersonRelationshipSchema,
    firstName: z.string().trim().min(1).max(100).optional(),
    lastName: z.string().trim().min(1).max(100).optional(),
    ssnLast4: z.string().regex(MIGRATION_SSN_LAST4_REGEX).optional(),
    fullSsn: z.string().regex(MIGRATION_FULL_SSN_REGEX).optional(),
    idNumber: z.string().trim().min(1).max(32).optional(),
    driverId: z.string().trim().min(1).max(64).optional(),
    dob: MigrationDobSchema.optional(),
    address: MigrationAddressSchema.optional(),
    email: MigrationContactEmailSchema.optional(),
    phone: MigrationContactPhoneSchema.optional(),
})
    .strict()
    .superRefine((person, ctx) => {
    if (!person.ssnLast4 && !person.fullSsn) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['ssnLast4'],
            message: 'Either ssnLast4 or fullSsn is required for every person',
        });
    }
    if (person.ssnLast4 &&
        person.fullSsn &&
        !person.fullSsn.endsWith(person.ssnLast4)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['ssnLast4'],
            message: 'ssnLast4 must match the last four digits of fullSsn',
        });
    }
});
export const MigrationBankAccountRequestSchema = z
    .object({
    holderName: z.string().trim().min(1).max(100),
    holderType: MigrationBankHolderTypeSchema,
    routingNumber: z
        .string()
        .trim()
        .regex(/^\d{9}$/, 'US routing number must be nine digits'),
    /** Never returned by Stripe; must be re-entered. */
    accountNumber: z
        .string()
        .trim()
        .min(4)
        .max(17)
        .regex(/^\d+$/, 'account number must be digits'),
})
    .strict();
/**
 * Stripe services agreement re-acceptance by the merchant. `timestamp` is Unix
 * seconds and must be within the last 24 hours (the backend's existing
 * `checkTosTimestamp`). `ip` is optional: the backend uses the request ip in
 * production and `0.0.0.0` elsewhere, exactly as onboarding v2 does.
 */
export const MigrationTosAcceptanceSchema = z
    .object({
    timestamp: z.number().int().positive(),
    ip: z.string().trim().min(1).max(45).optional(),
})
    .strict();
export const ManagedAccountMigrationRequestSchema = z
    .object({
    /** Must equal the business's currently linked account (checked live). */
    sourceAccountId: StripeAccountIdSchema,
    /** Never readable from Stripe (`company.tax_id_provided` only). */
    businessTaxId: z.string().trim().min(1).max(32),
    business: MigrationBusinessOverridesSchema.optional(),
    persons: z.array(MigrationPersonRequestSchema).min(1),
    bankAccount: MigrationBankAccountRequestSchema,
    tosAcceptance: MigrationTosAcceptanceSchema,
    /** Explicit operator confirmation; the request is refused without it. */
    confirm: z.literal(true),
})
    .strict()
    .superRefine((request, ctx) => {
    const representatives = request.persons.filter((person) => person.relationship.representative);
    if (representatives.length !== 1) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['persons'],
            message: 'Exactly one person must be the representative',
        });
    }
    const ids = request.persons.map((person) => person.sourcePersonId);
    if (new Set(ids).size !== ids.length) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['persons'],
            message: 'sourcePersonId must be unique',
        });
    }
});
// ---------------------------------------------------------------------------
// 4. Migration result
// ---------------------------------------------------------------------------
export const ManagedAccountMigrationResultSchema = z
    .object({
    businessId: BusinessIdSchema,
    /** The legacy account, left untouched and no longer linked. */
    previousAccountId: StripeAccountIdSchema,
    newAccountId: StripeAccountIdSchema,
    profile: ManagedAccountMigrationTargetProfileSchema,
    /** Person ids created on the new account, in request order. */
    personIds: z.array(z.string().min(1)),
    /** `true` once `All_Payment_Accounts.providerAccountId` points at the new account. */
    linked: z.boolean(),
    /** Live eligibility of the NEW account right after relink + refetch. */
    eligibility: AdminPayformsEligibilityResponseSchema,
    /**
     * Payment-method domain provisioning on the NEW account (DEC-P2-18),
     * performed automatically after relink. A failed provisioning keeps the
     * account linked; the status is `PROVIDER_UNAVAILABLE` or `MISSING` and
     * the remediation is the ensure action.
     */
    paymentDomains: PaymentMethodDomainEligibilitySchema.optional(),
    /**
     * `true` when the account is linked AND its payment-method domains are
     * VERIFIED (or NOT_APPLICABLE in an environment without required
     * domains). `false` means "migrated, setup incomplete — run ensure".
     */
    paymentSetupComplete: z.boolean(),
    warnings: z.array(z.string()),
    migratedAt: IsoTimestampSchema,
})
    .strict();
// ---------------------------------------------------------------------------
// Fixtures (fictitious ids; no real accounts, persons or secrets)
// ---------------------------------------------------------------------------
export const PXE_FIXTURE_MIGRATION_BUSINESS_ID = '00000000-0000-4000-8000-00000000abcd';
export const PXE_FIXTURE_MIGRATION_SOURCE_ACCOUNT_ID = 'acct_1ExampleLegacy00';
export const PXE_FIXTURE_MIGRATION_NEW_ACCOUNT_ID = 'acct_1ExampleManaged0';
export const PXE_FIXTURE_MIGRATION_SOURCE_PERSON_ID = 'person_1ExampleOwner00';
export const PXE_FIXTURE_MIGRATION_NEW_PERSON_ID = 'person_1ExampleOwner01';
export const PXE_FIXTURE_MIGRATION_GENERATED_AT = '2026-09-14T12:00:00.000Z';
const legacySnapshot = {
    profile: ConnectedAccountProfileEnum.LEGACY_CUSTOM,
    feesPayer: 'application_custom',
    lossesPayments: 'application',
    requirementCollection: 'application',
    stripeDashboardType: 'none',
    capturedAt: PXE_FIXTURE_MIGRATION_GENERATED_AT,
};
const managedSnapshot = {
    profile: ConnectedAccountProfileEnum.POINTCRAFT_MANAGED_DIRECT,
    feesPayer: 'application',
    lossesPayments: 'application',
    requirementCollection: 'application',
    stripeDashboardType: 'none',
    capturedAt: PXE_FIXTURE_MIGRATION_GENERATED_AT,
};
const fixtureAddress = {
    street1: '1 Example Street',
    street2: null,
    city: 'Austin',
    state: 'TX',
    postalCode: '78701',
    country: 'US',
};
export const PXE_FIXTURE_ADMIN_PAYFORMS_ELIGIBILITY_LEGACY_RESPONSE = {
    state: MerchantEligibilityStateEnum.NOT_ELIGIBLE,
    reason: AccountNotDirectEligibleReasonEnum.LEGACY_ACCOUNT_CONFIGURATION,
    message: 'The connected Stripe account is not platform-controlled (fee payer: application_custom); direct charges require a POINTCRAFT_MANAGED_DIRECT account.',
    accountId: PXE_FIXTURE_MIGRATION_SOURCE_ACCOUNT_ID,
    snapshot: legacySnapshot,
    live: true,
    checkedAt: PXE_FIXTURE_MIGRATION_GENERATED_AT,
};
export const PXE_FIXTURE_ADMIN_PAYFORMS_ELIGIBILITY_RESPONSE = {
    state: MerchantEligibilityStateEnum.ELIGIBLE,
    reason: null,
    message: null,
    accountId: PXE_FIXTURE_MIGRATION_NEW_ACCOUNT_ID,
    snapshot: managedSnapshot,
    live: true,
    checkedAt: PXE_FIXTURE_MIGRATION_GENERATED_AT,
};
export const PXE_FIXTURE_MIGRATION_PREVIEW = {
    businessId: PXE_FIXTURE_MIGRATION_BUSINESS_ID,
    sourceAccountId: PXE_FIXTURE_MIGRATION_SOURCE_ACCOUNT_ID,
    sourceSnapshot: legacySnapshot,
    targetProfile: MANAGED_ACCOUNT_MIGRATION_TARGET_PROFILE,
    status: ManagedAccountMigrationStatusEnum.READY,
    business: {
        name: 'Example Grocery LLC',
        url: 'https://example.test',
        email: 'business@example.test',
        phone: '+15125550100',
        description: 'Grocery store',
        mcc: '5411',
        address: fixtureAddress,
        statementDescriptor: 'EXAMPLE GROCERY',
    },
    persons: [
        {
            sourcePersonId: PXE_FIXTURE_MIGRATION_SOURCE_PERSON_ID,
            firstName: 'Jane',
            lastName: 'Example',
            email: 'jane@example.test',
            phone: '+15125550101',
            dob: { day: 1, month: 1, year: 1980 },
            address: fixtureAddress,
            relationship: {
                representative: true,
                owner: true,
                executive: false,
                title: 'Owner',
                percentOwnership: 100,
            },
            provided: {
                ssnLast4: true,
                idNumber: true,
                dob: true,
                address: true,
            },
            requiredInputs: ['ssn'],
        },
    ],
    bankAccount: {
        holderName: 'Example Grocery LLC',
        holderType: 'company',
        routingNumber: '110000000',
        last4: '6789',
        bankName: 'STRIPE TEST BANK',
    },
    requiredInputs: [
        {
            key: 'businessTaxId',
            label: 'Business tax id (EIN)',
            reason: 'Stripe only reports company.tax_id_provided; the number is never returned.',
        },
        {
            key: buildMigrationPersonInputKey(PXE_FIXTURE_MIGRATION_SOURCE_PERSON_ID, 'ssn'),
            label: 'Jane Example — SSN (last four or full)',
            reason: 'Stripe only reports ssn_last_4_provided / id_number_provided; the number is never returned.',
        },
        {
            key: 'bankAccount.accountNumber',
            label: 'Bank account number (routing 110000000, ending 6789)',
            reason: 'Bank account number cannot be read from Stripe.',
        },
        {
            key: 'tosAcceptance',
            label: 'Stripe services agreement re-acceptance',
            reason: 'Merchant must re-accept the Stripe services agreement.',
        },
    ],
    warnings: [
        'Bank account number cannot be read from Stripe',
        'The legacy account keeps its history and stays unlinked',
        'Merchant must re-accept the Stripe services agreement',
    ],
    generatedAt: PXE_FIXTURE_MIGRATION_GENERATED_AT,
};
/** Fictitious inputs (Stripe documented test values), never real data. */
export const PXE_FIXTURE_MIGRATION_REQUEST = {
    sourceAccountId: PXE_FIXTURE_MIGRATION_SOURCE_ACCOUNT_ID,
    businessTaxId: '000000000',
    business: { statementDescriptor: 'EXAMPLE GROCERY' },
    persons: [
        {
            sourcePersonId: PXE_FIXTURE_MIGRATION_SOURCE_PERSON_ID,
            relationship: {
                representative: true,
                owner: true,
                executive: false,
                title: 'Owner',
                percentOwnership: 100,
            },
            ssnLast4: '0000',
        },
    ],
    bankAccount: {
        holderName: 'Example Grocery LLC',
        holderType: 'company',
        routingNumber: '110000000',
        accountNumber: '000123456789',
    },
    tosAcceptance: { timestamp: 1789000000 },
    confirm: true,
};
export const PXE_FIXTURE_MIGRATION_RESULT = {
    businessId: PXE_FIXTURE_MIGRATION_BUSINESS_ID,
    previousAccountId: PXE_FIXTURE_MIGRATION_SOURCE_ACCOUNT_ID,
    newAccountId: PXE_FIXTURE_MIGRATION_NEW_ACCOUNT_ID,
    profile: MANAGED_ACCOUNT_MIGRATION_TARGET_PROFILE,
    personIds: [PXE_FIXTURE_MIGRATION_NEW_PERSON_ID],
    linked: true,
    eligibility: PXE_FIXTURE_ADMIN_PAYFORMS_ELIGIBILITY_RESPONSE,
    paymentDomains: PXE_FIXTURE_PAYMENT_DOMAINS_VERIFIED,
    paymentSetupComplete: true,
    warnings: ['The legacy account keeps its history and stays unlinked'],
    migratedAt: PXE_FIXTURE_MIGRATION_GENERATED_AT,
};
/** Migration completed but domain provisioning failed: linked, setup incomplete. */
export const PXE_FIXTURE_MIGRATION_RESULT_SETUP_INCOMPLETE = {
    ...PXE_FIXTURE_MIGRATION_RESULT,
    eligibility: {
        ...PXE_FIXTURE_ADMIN_PAYFORMS_ELIGIBILITY_RESPONSE,
        state: MerchantEligibilityStateEnum.NOT_ELIGIBLE,
        reason: AccountNotDirectEligibleReasonEnum.PAYMENT_DOMAINS_MISSING,
        message: 'The PointPay checkout domain is not registered on the connected account.',
        paymentDomains: PXE_FIXTURE_PAYMENT_DOMAINS_MISSING,
    },
    paymentDomains: PXE_FIXTURE_PAYMENT_DOMAINS_MISSING,
    paymentSetupComplete: false,
    warnings: [
        'The legacy account keeps its history and stays unlinked',
        'Payment-method domains could not be provisioned; run ensure payment domains',
    ],
};
